import {
	type PartnerGeneralResponseDto,
	PartnerSkills,
	PartnerStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { getFormValuesDiff } from "Infrastructure/Utilities/Forms/Helpers/getFormValuesDiff";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useMarketListQuery } from "Market/Api/Query/useMarketListQuery";
import { useMarketRegionList } from "Market/Api/Query/useMarketRegionList";
import { useUpdateGeneralInfo } from "Partner/Api/Mutation/updatePartnerGeneralMutation";
import {
	getPartnerGeneralInfoSchema,
	type PartnerGeneralFormModel,
} from "Partner/Components/Form/Schema/partnerGeneralInfoSchema";
import { useVerticalListQuery } from "Settings/Modules/OrderSetup/Api/Query/useVerticalListQuery";
import Checkbox from "Shared/Components/Form/CustomFormComponent/FormCheckboxBoolean";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
	Button,
	FormControlLabel,
	Grid2,
	IconButton,
	Radio,
	Typography,
} from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";

const generalInfoResources = Resources.base.partner.detail.generalInfo;
const selectOptionsResource = Resources.base.partner.selectOptions;

type GeneralInfoFormProps = {
	data: PartnerGeneralResponseDto;
	partnerId: number;
	isFormDisabled?: boolean;
	serviceAreas: Array<{
		marketRegionId: number;
		originAddress: string;
		isPrimary: boolean;
		isActive: boolean;
	}>;
};

// TODO: clicking on verticals adds the same verticals to the array and cannot be unchecked
export function GeneralInfoForm({
	data,
	partnerId,
	isFormDisabled,
	serviceAreas,
}: GeneralInfoFormProps) {
	const { t } = useResource();
	const { data: verticalsData, isLoading: isLoadingVerticals } =
		useVerticalListQuery();

	const form = useForm<PartnerGeneralFormModel>({
		resolver: zodResolver(getPartnerGeneralInfoSchema(t)),
		defaultValues: {
			...data,
			serviceAreas,
			verticals: data.verticals?.map((vertical) => vertical.id) || [],
			skills: data.skills ?? [],
		},
	});

	const {
		watch,
		control,
		formState: { errors, dirtyFields, isDirty },
		setValue,
	} = form;
	const {
		fields: serviceAreaFields,
		append,
		remove,
	} = useFieldArray({
		control,
		name: "serviceAreas",
	});

	const selectedMarketId = watch("marketId");

	const { data: marketList } = useMarketListQuery();
	const {
		data: marketRegionList,
		refetch: refetchMarketRegions,
		isLoading: isLoadingMarketRegions,
	} = useMarketRegionList(selectedMarketId);

	const { mutate: updateGeneralInfo, isPending } =
		useUpdateGeneralInfo(partnerId);

	const { handleServerError } = useHandleServerError(form);

	const handleMarketChange = () => {
		setValue("marketRegionId", undefined);
		setValue("serviceAreas", []);
		refetchMarketRegions();
	};

	const handleSubmitUpdate = () => {
		handleServerError((data) => {
			const changedValues = getFormValuesDiff(dirtyFields, data);
			if (Object.keys(changedValues).length === 0 || !data.marketRegionId)
				return;
			return updateGeneralInfo({
				...changedValues,
				marketId: data.marketId,
				marketRegionId: data.marketRegionId,
				verticals: data.verticals,
				skills: data.skills,
				serviceAreas: data.serviceAreas,
			} as Parameters<typeof updateGeneralInfo>[0]);
		});
	};

	return (
		<>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="first-name"
						name="name"
						label={t(generalInfoResources.name)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Typography variant="h6" mb={1}>
						{t(generalInfoResources.serviceAreas)}
					</Typography>
					<Grid2 container spacing={2}>
						{serviceAreaFields.map((field, index) => (
							<Grid2
								container
								spacing={1}
								size={{ xs: 12 }}
								key={field.id}
								alignItems="end"
							>
								<Grid2 size={{ xs: 12, md: 4 }}>
									<Select
										id={`service-area-region-${index}`}
										label={t(generalInfoResources.serviceAreaRegion)}
										fullWidth
										options={
											marketRegionList?.map((region) => ({
												label: region.name,
												value: region.id,
											})) ?? []
										}
										variant="outlined"
										name={`serviceAreas.${index}.marketRegionId`}
										control={control}
										errors={errors}
										numeric
										disabled={isFormDisabled || isLoadingMarketRegions}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 5 }}>
									<Input
										id={`service-area-origin-${index}`}
										name={`serviceAreas.${index}.originAddress`}
										label={t(generalInfoResources.serviceAreaOrigin)}
										variant="outlined"
										control={control}
										errors={errors}
										fullWidth
										disabled={isFormDisabled}
									/>
								</Grid2>
								<Grid2 size={{ xs: 10, md: 2 }}>
									<Controller
										control={control}
										name={`serviceAreas.${index}.isPrimary`}
										render={({ field: { value } }) => (
											<FormControlLabel
												label={t(generalInfoResources.primaryServiceArea)}
												control={
													<Radio
														checked={value}
														disabled={isFormDisabled}
														onChange={() => {
															serviceAreaFields.forEach((_, areaIndex) =>
																setValue(
																	`serviceAreas.${areaIndex}.isPrimary`,
																	areaIndex === index,
																	{
																		shouldDirty: true,
																	},
																),
															);
															const regionId = watch(
																`serviceAreas.${index}.marketRegionId`,
															);
															if (regionId)
																setValue("marketRegionId", regionId, {
																	shouldDirty: true,
																});
														}}
													/>
												}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 2, md: 1 }}>
									<IconButton
										aria-label={t(generalInfoResources.removeServiceArea)}
										onClick={() => remove(index)}
										disabled={isFormDisabled || serviceAreaFields.length === 1}
									>
										<DeleteOutlineIcon />
									</IconButton>
								</Grid2>
							</Grid2>
						))}
					</Grid2>
					<Button
						variant="outlined"
						sx={{ mt: 1 }}
						disabled={isFormDisabled || !selectedMarketId}
						onClick={() =>
							append({
								marketRegionId: 0,
								originAddress: "",
								isPrimary: false,
								isActive: true,
							})
						}
					>
						{t(generalInfoResources.addServiceArea)}
					</Button>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="middle-name"
						name="middleName"
						label={t(generalInfoResources.middleName)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="last-name"
						name="surname"
						label={t(generalInfoResources.surname)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Select
						id="status"
						label={t(generalInfoResources.status)}
						fullWidth
						options={createSelectOptions(
							PartnerStatus,
							t,
							selectOptionsResource.status,
						)}
						variant="outlined"
						name="status"
						control={control}
						errors={errors}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="phone"
						name="phone"
						label={t(generalInfoResources.phone)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="email"
						name="email"
						label={t(generalInfoResources.email)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Checkbox
						id="can-train-days"
						name="isCanTrainDay"
						label={t(generalInfoResources.isCanTrainDay)}
						control={control}
						errors={errors}
						disabled={isFormDisabled}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Select
						id="marketId"
						label={t(generalInfoResources.market)}
						fullWidth
						options={
							marketList?.map((m) => {
								return {
									label: m.name,
									value: m.id,
								};
							}) ?? []
						}
						variant="outlined"
						name="marketId"
						control={control}
						errors={errors}
						numeric
						onChange={handleMarketChange}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Select
						id="verticals"
						label={t(generalInfoResources.verticals)}
						fullWidth
						options={
							verticalsData?.map((vertical) => ({
								label:
									vertical.name.charAt(0).toUpperCase() +
									vertical.name.slice(1),
								value: vertical.id,
							})) ?? []
						}
						variant="outlined"
						name="verticals"
						control={control}
						errors={errors}
						multiple
						disabled={isLoadingVerticals || isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Select
						id="skills"
						label={t(generalInfoResources.skills)}
						fullWidth
						options={createSelectOptions(
							PartnerSkills,
							t,
							selectOptionsResource.skills,
						)}
						variant="outlined"
						name="skills"
						control={control}
						errors={errors}
						multiple
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						isNumber
						type="number"
						id="team-size"
						name="teamSize"
						label={t(generalInfoResources.teamSize)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6 }}>
					<Input
						id="profile-link"
						name="profileLink"
						label={t(generalInfoResources.profileLink)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Input
						id="notes"
						name="notes"
						label={t(generalInfoResources.notes)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						multiline
						rows={3}
						maxRows={6}
						disabled={isFormDisabled}
					/>
				</Grid2>
			</Grid2>
			<Button
				variant="contained"
				color="primary"
				onClick={handleSubmitUpdate}
				disabled={isFormDisabled || !isDirty}
				loading={isPending}
				sx={{ mt: 2 }}
			>
				{t(generalInfoResources.update)}
			</Button>
		</>
	);
}
